import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  carBrandAPI,
  carModelAPI,
  carVariantAPI,
  bikeBrandAPI,
  bikeModelAPI,
  bikeVariantAPI,
  carServiceCenterAPI,
  carDealerAPI,
  bikeServiceCenterAPI,
  bikeDealerAPI,
  drivingSchoolAPI,
  rtoAPI,
  questionBankAPI,
  practiceQuestionAPI,
  examQuestionAPI,
  trafficSignalAPI, vehicleInfoAPI
} from '../services/api';

// Bar Chart Component
const BarChart = ({ data, colors, height = 200 }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);

  return (
    <div style={{ width: '100%', height: `${height}px`, position: 'relative' }}>
      {/* Y-axis labels */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 30, width: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '11px', color: '#64748b' }}>
        <span>{maxValue}</span>
        <span>{Math.round(maxValue / 2)}</span>
        <span>0</span>
      </div>

      {/* Chart area */}
      <div style={{ marginLeft: '45px', height: `${height - 30}px`, display: 'flex', alignItems: 'flex-end', gap: '8px', borderBottom: '1px solid #e2e8f0' }}>
        {data.map((item, index) => {
          const barHeight = (item.value / maxValue) * 100;
          return (
            <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{item.value}</span>
              <div
                style={{
                  width: '100%',
                  maxWidth: '60px',
                  height: `${barHeight}%`,
                  minHeight: '4px',
                  background: `linear-gradient(180deg, ${colors[index % colors.length]}, ${colors[index % colors.length]}dd)`,
                  borderRadius: '6px 6px 0 0',
                  transition: 'height 0.5s ease'
                }}
              />
            </div>
          );
        })}
      </div>

      {/* X-axis labels */}
      <div style={{ marginLeft: '45px', display: 'flex', gap: '8px', marginTop: '8px' }}>
        {data.map((item, index) => (
          <div key={index} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

// Donut Chart Component
const DonutChart = ({ data, size = 180 }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let currentAngle = -90;
  const strokeWidth = 35;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const segments = data.map((item) => {
    const percentage = total > 0 ? (item.value / total) * 100 : 0;
    const strokeDasharray = (percentage / 100) * circumference;
    const strokeDashoffset = 0;
    const rotation = currentAngle;
    currentAngle += (percentage / 100) * 360;

    return {
      ...item,
      percentage,
      strokeDasharray,
      strokeDashoffset,
      rotation
    };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {segments.map((segment, index) => (
            <circle
              key={index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${segment.strokeDasharray} ${circumference}`}
              transform={`rotate(${segment.rotation} ${size / 2} ${size / 2})`}
              style={{ transition: 'stroke-dasharray 0.5s ease' }}
            />
          ))}
        </svg>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>{total}</div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>Total</div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {segments.map((segment, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: segment.color }} />
            <span style={{ fontSize: '13px', color: '#475569' }}>{segment.label}</span>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b', marginLeft: 'auto' }}>{segment.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Summary Card Component
// Card icons. Emoji were used before, which render differently on every OS
// (and as flat glyphs on Windows), so they never matched the sidebar's line
// icons. These inherit currentColor, so each card tints its own.
const Icon = {
  vehicles: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14M5 17a2 2 0 01-2-2v-3l2-5a2 2 0 012-1.5h10A2 2 0 0119 7l2 5v3a2 2 0 01-2 2" />
      <circle cx="7.5" cy="17" r="1.5" /><circle cx="16.5" cy="17" r="1.5" />
    </svg>
  ),
  services: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a4 4 0 01-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 015.4-5.4l-2.6 2.6-1.4-1.4 2.6-2.6z" />
    </svg>
  ),
  content: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
    </svg>
  ),
  rc: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <circle cx="8" cy="11" r="2" /><path d="M4.5 16c.6-1.6 2-2.4 3.5-2.4s2.9.8 3.5 2.4" />
      <line x1="14.5" y1="10" x2="19" y2="10" /><line x1="14.5" y1="13.5" x2="19" y2="13.5" />
    </svg>
  ),
  all: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  // Smaller set for the quick-stat pills below the cards.
  brands: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.6 13.4L13.4 20.6a2 2 0 01-2.8 0l-7.2-7.2A2 2 0 013 12V5a2 2 0 012-2h7a2 2 0 011.4.6l7.2 7.2a2 2 0 010 2.6z" />
      <circle cx="7.5" cy="7.5" r="1.2" />
    </svg>
  ),
  models: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14M5 17a2 2 0 01-2-2v-3l2-5a2 2 0 012-1.5h10A2 2 0 0119 7l2 5v3a2 2 0 01-2 2" />
      <circle cx="7.5" cy="17" r="1.5" /><circle cx="16.5" cy="17" r="1.5" />
    </svg>
  ),
  variants: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  ),
  rto: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
    </svg>
  ),
  school: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 14l9-5-9-5-9 5 9 5z" />
      <path d="M12 14l6.16-3.42a12.08 12.08 0 01.66 6.48A11.95 11.95 0 0012 20.05a11.95 11.95 0 00-6.82-3 12.08 12.08 0 01.66-6.47L12 14z" />
    </svg>
  ),
  dealer: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l1.5-5h15L21 9M3 9h18M3 9v11h18V9M8 20v-6h8v6" />
    </svg>
  ),
  wrench: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a4 4 0 01-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 015.4-5.4l-2.6 2.6-1.4-1.4 2.6-2.6z" />
    </svg>
  ),
  bike: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5.5" cy="17" r="3.5" /><circle cx="18.5" cy="17" r="3.5" />
      <path d="M5.5 17l4-8h5l3 8M12 9V6h3" />
    </svg>
  ),
  note: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  check: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  signal: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="16" rx="3" /><circle cx="12" cy="6" r="1.1" /><circle cx="12" cy="10" r="1.1" /><circle cx="12" cy="14" r="1.1" />
      <path d="M12 18v4" />
    </svg>
  ),
};

const SummaryCard = ({ icon, value, label, color, trend }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '20px',
        border: '1px solid #e2e8f0',
        boxShadow: isHovered ? '0 8px 25px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.3s ease',
        cursor: 'default'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
        }}>
          {icon}
        </div>
        {trend && (
          <span style={{
            fontSize: '12px',
            fontWeight: '500',
            color: trend > 0 ? '#10b981' : '#ef4444',
            backgroundColor: trend > 0 ? '#ecfdf5' : '#fef2f2',
            padding: '4px 8px',
            borderRadius: '12px'
          }}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div style={{ marginTop: '16px' }}>
        <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>{value.toLocaleString()}</div>
        <div style={{ fontSize: '14px', color: '#64748b', marginTop: '4px' }}>{label}</div>
      </div>
    </div>
  );
};

// Filter Button Component
const FilterButton = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '8px 16px',
      fontSize: '13px',
      fontWeight: '500',
      color: active ? '#ffffff' : '#64748b',
      backgroundColor: active ? '#4f46e5' : '#ffffff',
      border: active ? 'none' : '1px solid #e2e8f0',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
  >
    {label}
  </button>
);

// Chart Card Component
const ChartCard = ({ title, subtitle, children, action }) => (
  <div style={{
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: 0 }}>{title}</h3>
        {subtitle && <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

// Quick Stats Row
const QuickStatsRow = ({ items }) => (
  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
    {items.map((item, index) => (
      <div key={index} style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        backgroundColor: `${item.color}10`,
        borderRadius: '8px'
      }}>
        <span style={{ display: 'inline-flex', color: item.color }}>{item.icon}</span>
        <span style={{ fontSize: '14px', fontWeight: '600', color: item.color }}>{item.value}</span>
        <span style={{ fontSize: '13px', color: '#64748b' }}>{item.label}</span>
      </div>
    ))}
  </div>
);

function Dashboard() {
  const { admin } = useAuth();
  const [stats, setStats] = useState({
    carBrands: 0, carModels: 0, carVariants: 0, carServiceCenters: 0, carDealers: 0,
    bikeBrands: 0, bikeModels: 0, bikeVariants: 0, bikeServiceCenters: 0, bikeDealers: 0,
    drivingSchools: 0, rtoOffices: 0, questionBank: 0, practiceQuestions: 0, examQuestions: 0, trafficSignals: 0,
    vehicleInfo: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const [
        carBrandsRes, carModelsRes, carVariantsRes, carServiceCentersRes, carDealersRes,
        bikeBrandsRes, bikeModelsRes, bikeVariantsRes, bikeServiceCentersRes, bikeDealersRes,
        drivingSchoolsRes, rtoRes, questionBankRes, practiceQuestionsRes, examQuestionsRes, trafficSignalsRes,
        vehicleInfoRes
      ] = await Promise.all([
        carBrandAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        carModelAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        carVariantAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        carServiceCenterAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        carDealerAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        bikeBrandAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        bikeModelAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        bikeVariantAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        bikeServiceCenterAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        bikeDealerAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        drivingSchoolAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        rtoAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        questionBankAPI.getQuestions({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        practiceQuestionAPI.getQuestions({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        examQuestionAPI.getQuestions({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        trafficSignalAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } })),
        // The RC cache — every registration ever resolved.
        vehicleInfoAPI.getAll({ page: 1, limit: 1 }).catch(() => ({ data: { pagination: { total: 0 } } }))
      ]);

      setStats({
        carBrands: carBrandsRes.data.pagination?.total || 0,
        carModels: carModelsRes.data.pagination?.total || 0,
        carVariants: carVariantsRes.data.pagination?.total || 0,
        carServiceCenters: carServiceCentersRes.data.pagination?.total || 0,
        carDealers: carDealersRes.data.pagination?.total || 0,
        bikeBrands: bikeBrandsRes.data.pagination?.total || 0,
        bikeModels: bikeModelsRes.data.pagination?.total || 0,
        bikeVariants: bikeVariantsRes.data.pagination?.total || 0,
        bikeServiceCenters: bikeServiceCentersRes.data.pagination?.total || 0,
        bikeDealers: bikeDealersRes.data.pagination?.total || 0,
        drivingSchools: drivingSchoolsRes.data.pagination?.total || 0,
        rtoOffices: rtoRes.data.pagination?.total || 0,
        questionBank: questionBankRes.data.pagination?.total || 0,
        practiceQuestions: practiceQuestionsRes.data.pagination?.total || 0,
        examQuestions: examQuestionsRes.data.pagination?.total || 0,
        trafficSignals: trafficSignalsRes.data.pagination?.total || 0,
        vehicleInfo: vehicleInfoRes.data.pagination?.total || 0
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totalVehicles = stats.carBrands + stats.carModels + stats.carVariants + stats.bikeBrands + stats.bikeModels + stats.bikeVariants;
  const totalServices = stats.carServiceCenters + stats.carDealers + stats.bikeServiceCenters + stats.bikeDealers + stats.drivingSchools + stats.rtoOffices;
  const totalContent = stats.questionBank + stats.practiceQuestions + stats.examQuestions + stats.trafficSignals;

  // Chart data
  const carsVsBikesData = [
    { label: 'Car Brands', value: stats.carBrands },
    { label: 'Bike Brands', value: stats.bikeBrands },
    { label: 'Car Models', value: stats.carModels },
    { label: 'Bike Models', value: stats.bikeModels },
    { label: 'Car Variants', value: stats.carVariants },
    { label: 'Bike Variants', value: stats.bikeVariants }
  ];

  const servicesData = [
    { label: 'Car Service Centers', value: stats.carServiceCenters },
    { label: 'Car Dealers', value: stats.carDealers },
    { label: 'Bike Service Centers', value: stats.bikeServiceCenters },
    { label: 'Bike Dealers', value: stats.bikeDealers },
    { label: 'Driving Schools', value: stats.drivingSchools },
    { label: 'RTO Offices', value: stats.rtoOffices }
  ];

  const contentDistributionData = [
    { label: 'Question Bank', value: stats.questionBank, color: '#4f46e5' },
    { label: 'Practice Questions', value: stats.practiceQuestions, color: '#10b981' },
    { label: 'Exam Questions', value: stats.examQuestions, color: '#f59e0b' },
    { label: 'Traffic Signals', value: stats.trafficSignals, color: '#ec4899' }
  ];

  const vehicleDistributionData = [
    { label: 'Cars', value: stats.carBrands + stats.carModels + stats.carVariants, color: '#4f46e5' },
    { label: 'Bikes', value: stats.bikeBrands + stats.bikeModels + stats.bikeVariants, color: '#10b981' }
  ];

  // Filter chart data based on active filter
  const getFilteredBarData = () => {
    switch (activeFilter) {
      case 'cars':
        return [
          { label: 'Brands', value: stats.carBrands },
          { label: 'Models', value: stats.carModels },
          { label: 'Variants', value: stats.carVariants },
          { label: 'Service Centers', value: stats.carServiceCenters },
          { label: 'Dealers', value: stats.carDealers }
        ];
      case 'bikes':
        return [
          { label: 'Brands', value: stats.bikeBrands },
          { label: 'Models', value: stats.bikeModels },
          { label: 'Variants', value: stats.bikeVariants },
          { label: 'Service Centers', value: stats.bikeServiceCenters },
          { label: 'Dealers', value: stats.bikeDealers }
        ];
      case 'services':
        return servicesData;
      case 'content':
        return [
          { label: 'Question Bank', value: stats.questionBank },
          { label: 'Practice', value: stats.practiceQuestions },
          { label: 'Exam', value: stats.examQuestions },
          { label: 'Traffic Signs', value: stats.trafficSignals }
        ];
      default:
        return carsVsBikesData;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ width: '200px', height: '32px', backgroundColor: '#e2e8f0', borderRadius: '8px', marginBottom: '8px', animation: 'pulse 1.5s infinite' }} />
          <div style={{ width: '300px', height: '20px', backgroundColor: '#e2e8f0', borderRadius: '6px', animation: 'pulse 1.5s infinite' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ height: '140px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', animation: 'pulse 1.5s infinite' }} />
          ))}
        </div>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Dashboard</h1>
            <p style={{ color: '#64748b', marginTop: '4px', fontSize: '15px' }}>Welcome back, {admin?.name}! Here's your overview.</p>
          </div>
          <button
            onClick={fetchStats}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#4f46e5',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <SummaryCard icon={Icon.vehicles} value={totalVehicles} label="Total Vehicles" color="#4f46e5" />
        <SummaryCard icon={Icon.services} value={totalServices} label="Total Services" color="#10b981" />
        <SummaryCard icon={Icon.content} value={totalContent} label="Total Content" color="#f59e0b" />
        <SummaryCard icon={Icon.rc} value={stats.vehicleInfo} label="RC Records" color="#0ea5e9" />
        <SummaryCard icon={Icon.all} value={totalVehicles + totalServices + totalContent + stats.vehicleInfo} label="All Records" color="#ec4899" />
      </div>

      {/* Quick Stats */}
      <div style={{ marginBottom: '24px' }}>
        <QuickStatsRow items={[
          { icon: Icon.brands, value: stats.carBrands + stats.bikeBrands, label: 'Brands', color: '#4f46e5' },
          { icon: Icon.models, value: stats.carModels + stats.bikeModels, label: 'Models', color: '#10b981' },
          { icon: Icon.variants, value: stats.carVariants + stats.bikeVariants, label: 'Variants', color: '#f59e0b' },
          { icon: Icon.rto, value: stats.rtoOffices, label: 'RTO Offices', color: '#4338ca' },
          { icon: Icon.school, value: stats.drivingSchools, label: 'Driving Schools', color: '#06b6d4' }
        ]} />
      </div>

      {/* Charts Section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '24px' }}>
        {/* Bar Chart */}
        <ChartCard
          title="Data Overview"
          subtitle="Compare different categories"
          action={
            <div style={{ display: 'flex', gap: '8px' }}>
              <FilterButton label="All" active={activeFilter === 'all'} onClick={() => setActiveFilter('all')} />
              <FilterButton label="Cars" active={activeFilter === 'cars'} onClick={() => setActiveFilter('cars')} />
              <FilterButton label="Bikes" active={activeFilter === 'bikes'} onClick={() => setActiveFilter('bikes')} />
              <FilterButton label="Services" active={activeFilter === 'services'} onClick={() => setActiveFilter('services')} />
              <FilterButton label="Content" active={activeFilter === 'content'} onClick={() => setActiveFilter('content')} />
            </div>
          }
        >
          <BarChart
            data={getFilteredBarData()}
            colors={['#4f46e5', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#4338ca']}
            height={220}
          />
        </ChartCard>

        {/* Donut Charts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <ChartCard title="Vehicle Distribution" subtitle="Cars vs Bikes breakdown">
            <DonutChart data={vehicleDistributionData} size={150} />
          </ChartCard>
          <ChartCard title="Content Distribution" subtitle="Question bank and signals">
            <DonutChart data={contentDistributionData} size={150} />
          </ChartCard>
        </div>
      </div>

      {/* Detailed Stats Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        {/* Cars Section */}
        <ChartCard title="Cars" subtitle="Brands, Models & Services">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <StatItem icon={Icon.brands} label="Brands" value={stats.carBrands} color="#4f46e5" />
            <StatItem icon={Icon.models} label="Models" value={stats.carModels} color="#4338ca" />
            <StatItem icon={Icon.variants} label="Variants" value={stats.carVariants} color="#7C3AED" />
            <StatItem icon={Icon.wrench} label="Service Centers" value={stats.carServiceCenters} color="#f59e0b" />
            <StatItem icon={Icon.dealer} label="Dealers" value={stats.carDealers} color="#f97316" />
          </div>
        </ChartCard>

        {/* Bikes Section */}
        <ChartCard title="Bikes" subtitle="Brands, Models & Services">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <StatItem icon={Icon.brands} label="Brands" value={stats.bikeBrands} color="#10b981" />
            <StatItem icon={Icon.bike} label="Models" value={stats.bikeModels} color="#059669" />
            <StatItem icon={Icon.variants} label="Variants" value={stats.bikeVariants} color="#047857" />
            <StatItem icon={Icon.wrench} label="Service Centers" value={stats.bikeServiceCenters} color="#f59e0b" />
            <StatItem icon={Icon.dealer} label="Dealers" value={stats.bikeDealers} color="#f97316" />
          </div>
        </ChartCard>

        {/* RTO & Knowledge Section */}
        <ChartCard title="RTO & Knowledge Base" subtitle="Offices, Schools & Questions">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <StatItem icon={Icon.rto} label="RTO Offices" value={stats.rtoOffices} color="#4338ca" />
            <StatItem icon={Icon.school} label="Driving Schools" value={stats.drivingSchools} color="#a855f7" />
            <StatItem icon={Icon.content} label="Question Bank" value={stats.questionBank} color="#06b6d4" />
            <StatItem icon={Icon.note} label="Practice Questions" value={stats.practiceQuestions} color="#0891b2" />
            <StatItem icon={Icon.check} label="Exam Questions" value={stats.examQuestions} color="#14b8a6" />
            <StatItem icon={Icon.signal} label="Traffic Signals" value={stats.trafficSignals} color="#ec4899" />
          </div>
        </ChartCard>
      </div>
    </div>
  );
}

// Stat Item Component for detailed sections
const StatItem = ({ icon, label, value, color }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: `${color}08`,
    borderRadius: '10px',
    border: `1px solid ${color}20`
  }}>
    <div style={{
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: `${color}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#64748b' }}>{label}</div>
    </div>
  </div>
);

export default Dashboard;
